import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import ActionCard from "@/components/landing/ActionCard";
import StampCardCard from "@/components/landing/StampCardCard";
import ActionFilters from "@/components/landing/ActionFilters";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { api } from "@/api";
import { WalletCards } from "lucide-react";

// Use same API base logic as api.js
const getApiBase = () => {
  if (typeof window !== "undefined" && window.location && window.location.origin) {
    return `${window.location.origin}/api`;
  }
  return "/api";
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { customer, isAuthenticated: isCustomerAuthenticated, loading: customerAuthLoading } = useCustomerAuth();
  const { toast } = useToast();
  const [actions, setActions] = useState([]);
  const [stampCards, setStampCards] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [customerStampCardIds, setCustomerStampCardIds] = useState(() => new Set());
  // Track which customer ID's stamp cards have been loaded.
  // Derive activationStateLoading from this to avoid stale-state flicker between renders and effects.
  const [loadedForCustomerId, setLoadedForCustomerId] = useState(null);
  const activationStateLoading = customerAuthLoading || (isCustomerAuthenticated && loadedForCustomerId !== (customer?.id ?? null));
  const [activatingStampCardId, setActivatingStampCardId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: "all", // all, upcoming, active
    organizationId: null,
    search: "",
  });

  useEffect(() => {
    fetchData();
  }, [filters.status, filters.organizationId]);

  useEffect(() => {
    if (customerAuthLoading) {
      return;
    }
    if (!isCustomerAuthenticated) {
      setCustomerStampCardIds(new Set());
      setLoadedForCustomerId(null);
      return;
    }
    refreshCustomerActivationState();
  }, [customerAuthLoading, isCustomerAuthenticated, customer?.id]);

  const redirectToCustomerLogin = () => {
    const redirect = `/${typeof window !== "undefined" ? window.location.search : ""}`;
    navigate(`/customer/login?redirect=${encodeURIComponent(redirect)}`);
  };

  const refreshCustomerActivationState = async () => {
    const fetchedForId = customer?.id ?? null;
    try {
      const customerStampCardsRes = await api.customerStampCardsActive();

      const nextStampCardIds = new Set(
        (customerStampCardsRes?.stampCards || [])
          .map((entry) => entry?.stampCard?.id)
          .filter(Boolean)
      );
      setCustomerStampCardIds(nextStampCardIds);
    } catch (_) {
      // ignore refresh errors; activation handlers show own toasts
    } finally {
      setLoadedForCustomerId(fetchedForId);
    }
  };

  const handleActivateStampCard = async (card) => {
    if (!isCustomerAuthenticated) {
      redirectToCustomerLogin();
      return;
    }
    if (!card?.id) return;
    if (customerStampCardIds.has(card.id)) {
      toast({ title: "Stempelkarte bereits hinzugefügt" });
      return;
    }

    setActivatingStampCardId(card.id);
    try {
      const response = await api.customerStampCardActivate(card.id);
      setCustomerStampCardIds((prev) => {
        const next = new Set(prev);
        next.add(card.id);
        return next;
      });
      toast({
        title: "Stempelkarte hinzugefügt",
        description: response?.newsletterResubscribed
          ? "Du bist dabei wieder für den Newsletter angemeldet worden."
          : "Stempelkarte wurde aktiviert.",
      });
    } catch (error) {
      const code = error?.payload?.code;
      if (code === "ALREADY_ACTIVATED") {
        setCustomerStampCardIds((prev) => {
          const next = new Set(prev);
          next.add(card.id);
          return next;
        });
        toast({ title: "Stempelkarte bereits hinzugefügt" });
      } else {
        toast({ title: "Aktivierung fehlgeschlagen", description: error?.message || "Unbekannter Fehler" });
      }
    } finally {
      setActivatingStampCardId(null);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const apiBase = getApiBase();
      const params = new URLSearchParams();
      if (filters.status !== "all") params.append("status", filters.status);
      if (filters.organizationId) params.append("organizationId", filters.organizationId);

      const [actionsRes, stampCardsRes, orgsRes] = await Promise.all([
        fetch(`${apiBase}/landingpage/actions?${params}`),
        fetch(`${apiBase}/landingpage/stamp-cards?${params}`),
        fetch(`${apiBase}/landingpage/organizations`),
      ]);

      if (actionsRes.ok) {
        const data = await actionsRes.json();
        setActions(data.actions || []);
      }

      if (stampCardsRes.ok) {
        const data = await stampCardsRes.json();
        setStampCards(data.stampCards || []);
      }

      if (orgsRes.ok) {
        const data = await orgsRes.json();
        setOrganizations(data.organizations || []);
      }
    } catch (error) {
      console.error("Failed to fetch landing page data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter by search term
  const filteredActions = actions.filter((action) => {
    if (!filters.search) return true;
    const searchLower = filters.search.toLowerCase();
    return (
      action.title.toLowerCase().includes(searchLower) ||
      action.organization.name.toLowerCase().includes(searchLower) ||
      action.campaign.name.toLowerCase().includes(searchLower)
    );
  });

  const filteredStampCards = stampCards.filter((card) => {
    if (!filters.search) return true;
    const searchLower = filters.search.toLowerCase();
    return (
      card.name.toLowerCase().includes(searchLower) ||
      card.company?.name?.toLowerCase().includes(searchLower) ||
      (card.description || "").toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Top Navbar */}
      <nav className="bg-background border-b border-border sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 flex items-center justify-between" style={{ height: '52px' }}>
          <a href="/" className="flex items-center gap-2 text-sm font-semibold text-foreground hover:opacity-80 transition-opacity">
            <img src="/favicon.svg" alt="Scanmirein" className="w-7 h-7" />
            <span className="tracking-tight">Scanmirein</span>
          </a>
          {isCustomerAuthenticated ? (
            <button
              onClick={() => navigate('/customer/dashboard')}
              className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-full transition-colors"
            >
              <WalletCards className="w-4 h-4" />
              Meine Wallet
            </button>
          ) : (
            <button
              onClick={redirectToCustomerLogin}
              className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-full transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              Anmelden
            </button>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-4">Aktuelle Aktionen &amp; Angebote</h1>
            <p className="text-xl text-blue-100 mb-8">
              Entdecke exklusive Rabatte und Aktionen von unseren Partner-Organisationen
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <ActionFilters
              filters={filters}
              setFilters={setFilters}
              organizations={organizations}
            />
          </div>

          {/* Actions Grid */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredActions.length === 0 && filteredStampCards.length === 0 ? (
              <div className="text-center py-20">
                <div className="text-muted-foreground text-6xl mb-4">🔍</div>
                <h3 className="text-2xl font-semibold text-foreground mb-2">
                  Keine Aktionen gefunden
                </h3>
                <p className="text-muted-foreground">
                  Versuche deine Filter anzupassen oder komm später wieder vorbei.
                </p>
              </div>
            ) : (
              <div className="space-y-10">
                {filteredActions.length > 0 && (
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-4">Aktionen</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {filteredActions.map((action) => (
                        <ActionCard key={action.id} action={action} />
                      ))}
                    </div>
                  </div>
                )}

                {filteredStampCards.length > 0 && (
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-4">Stempelkarten</h3>
                    {!isCustomerAuthenticated && (
                      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                        Für „Stempelkarte hinzufügen" brauchst du einen Useraccount und musst angemeldet sein.
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {filteredStampCards.map((card) => (
                        <StampCardCard
                          key={card.id}
                          card={card}
                          isCustomerAuthenticated={isCustomerAuthenticated}
                          isActivated={customerStampCardIds.has(card.id)}
                          activationStateLoading={activationStateLoading}
                          activating={activatingStampCardId === card.id}
                          onActivate={handleActivateStampCard}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
